import { AbstractControl, UntypedFormControl } from '@angular/forms';
import {
  ChangeDetectorRef,
  Directive,
  HostBinding,
  Injector,
  Input,
  OnInit
} from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { CustomValueAccessorComponent } from './custom-value-accessor.component';
import { v4 as uuid } from 'uuid';

/**
 * To be used only for form components, not for subforms with only 1 field
 */
@Directive()
// tslint:disable-next-line
export abstract class MonoFieldValueAccessorComponent<
  TYPE_PARENT,
  TYPE_ENFANT = TYPE_PARENT
>
  extends CustomValueAccessorComponent<TYPE_PARENT>
  implements OnInit
{
  constructor(
    private changeDetector: ChangeDetectorRef,
    protected injector?: Injector,
  ) {
    super(changeDetector);
  }

  internalField: UntypedFormControl = new UntypedFormControl();
  uuidInput: string = uuid();

  /**
   * Label of the component to be displayed
   */
  @Input()
  labelTitle: string;

  @Input()
  subLabelTitle: string;

  /**
   * Default value: false, if true, the label will not be displayed.
   */
  @Input()
  labelHidden = false;

  /**
   * Class of the sub-form
   */
  @Input()
  classField = '';

  /**
   * Name of the sub-form
   */
  @Input()
  nameField = '';

  /**
   * If defined, add a 'null' value by default
   */
  @Input()
  placeholder = '';

  /**
   * Defines whether the field is "small".
   */
  @Input()
  isSmall = false;

  @Input()
  readonly = false;

  /**
   * Adds the sd-form-field attribute to all form components to add style
   */
  @HostBinding('attr.sd-form-field') sdFormField = '';

  @Input()
  class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return this.getClasses();
  }

  getClasses(): string {
    return [this.labelTitle && !this.labelHidden ? 'has-libelle' : '']
      .filter(Boolean)
      .join(' ');
  }

  ngOnInit() {
    this.markForCheck(this.updateParent(this.internalField));
  }

  /**
   * Mapping method to be implemented to map values from parent to child
   */
  abstract mappingParentToChild(item: TYPE_PARENT): TYPE_ENFANT;

  /**
   * Mapping method to be implemented to map values from child to parent
   */
  abstract mappingChildToParent(item: TYPE_ENFANT): TYPE_PARENT;

  /**
   * Subscribes to changes in the internal form to update the parent value
   */
  updateParent(control: AbstractControl): Observable<TYPE_PARENT> {
    return control.valueChanges.pipe(
      map((value) => this.mappingChildToParent(value)),
      tap((value) => this.propagateChange(value)),
      tap(() => this.propagateTouched()),
    );
  }

  /**
   * Patch the internal form with the value mapped from the parent
   * (does not emit an event so as not to loop on value changes)
   */
  writeValue(value: TYPE_PARENT): void {
    if (this.readonly === false) {
      this.internalField.setValue(this.mappingParentToChild(value), {
        emitEvent: false
      });
      this.changeDetector.markForCheck();
    }
  }

  /**
   * Enables and disables the internal form when the external form is disabled
   * /!\ Must not issue an event to avoid looping
   */
  setDisabledState(isDisabled: boolean): void {
    // /!\ If it's not at "false", it can make an infinite loop because it emits a change of value
    // that one listens to to set the value, which triggers a change of value again
    const options = { emitEvent: false };

    if (isDisabled) {
      this.internalField.disable(options);
    } else {
      this.internalField.enable(options);
    }

    this.changeDetector.markForCheck();
  }
}
